import React from 'react';

import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  WarningRounded as WarningRoundedIcon
} from '@mui/icons-material';
import { Alert, Button as MuiButton, Slide, Snackbar as MuiSnackbar } from '@mui/material';

import { DARK_GREY, GREEN, RED } from '../../ThemeContext/constants';

import type { SlideProps } from '@mui/material';

export type SnackbarState = 'success' | 'warning' | 'error';
export interface SnackbarInput {
  message: string;
  state: SnackbarState;
}
export type SnackbarSetter = (snackbarState: SnackbarInput) => void;

export interface SnackbarProps {
  onClose?: () => void;
  message: SnackbarInput['message'];
  state: SnackbarInput['state'];
  durationMs?: number;
}

const TransitionDown = (props: SlideProps) => <Slide direction="down" {...props} />;

const getColors = (state: 'success' | 'warning' | 'error') => {
  switch (state) {
    case 'success':
      return { backgroundColor: '#EFFDF3', color: GREEN, borderColor: 'transparent' };
    case 'warning':
      return { backgroundColor: '#FDF6EE', color: '#C9781D', borderColor: 'transparent' };
    case 'error':
      return { backgroundColor: '#FCF2F2', color: RED, borderColor: 'transparent' };
    default:
      return { backgroundColor: '#FDF6EE', color: '#C9781D', borderColor: 'transparent' };
  }
};

const getSnackbarIcon = (state: 'success' | 'warning' | 'error') => {
  switch (state) {
    case 'success':
      return <CheckCircleIcon style={{ fontSize: '24px', color: GREEN }} />;
    case 'warning':
      return <WarningRoundedIcon style={{ fontSize: '24px', color: '#C9781D' }} />;
    case 'error':
      return <CancelIcon style={{ fontSize: '24px', color: RED }} />;
    default:
      return <WarningRoundedIcon style={{ fontSize: '24px', color: '#C9781D' }} />;
  }
};

const Snackbar = ({
  message,
  state,
  durationMs = 2000,
  onClose
}: SnackbarProps): React.ReactElement | null => {
  const [displayed, setDisplayed] = React.useState<SnackbarInput | null>(() =>
    message ? { message, state } : null
  );
  const [prevSnackbar, setPrevSnackbar] = React.useState<SnackbarInput>({ message, state });

  if (message !== prevSnackbar.message || state !== prevSnackbar.state) {
    setPrevSnackbar({ message, state });
    if (message) {
      setDisplayed({ message, state });
    }
  }

  if (!displayed) {
    return null;
  }

  const colors = getColors(displayed.state);

  const snackbarStyles = {
    backgroundColor: colors.backgroundColor,
    color: colors.color,
    border: colors.borderColor !== 'transparent' ? `1px solid ${colors.borderColor}` : 'none',
    borderRadius: '6px',
    boxShadow: 'none',
    textTransform: 'none' as const,
    fontWeight: 700,
    fontSize: '16px',
    fontFamily: 'Source Sans Pro, sans-serif',
    width: '670px',
    maxWidth: '670px',
    display: 'flex',
    paddingBlock: '16px',
    alignItems: 'center',
    gap: '16px',
    paddingInline: '24px',
    '& .MuiAlert-message': {
      flex: 1,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      textAlign: 'left'
    }
  };

  return (
    <MuiSnackbar
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      autoHideDuration={durationMs}
      onClose={onClose}
      open={Boolean(message)}
      slotProps={{ transition: { onExited: () => setDisplayed(null) } }}
      slots={{ transition: TransitionDown }}
    >
      <Alert icon={getSnackbarIcon(displayed.state)} severity={displayed.state} sx={snackbarStyles}>
        <span>{displayed.message}</span>
        <MuiButton
          onClick={onClose}
          size="small"
          variant="text"
          sx={{
            color: DARK_GREY,
            '&:hover': { color: DARK_GREY },
            '&:focus': { boxShadow: 'none' }
          }}
        >
          DISMISS
        </MuiButton>
      </Alert>
    </MuiSnackbar>
  );
};

export default Snackbar;
